import { isOfflineMode } from "@/helpers/offlineMode";
import apiClient from "./apiClient";
import type {
  CreditAgency,
  CreditCheckDetail,
  CreditCheckSummary,
  CreditCheckPerson,
  CreditAccountPaymentHistoryItem,
  CreditAccountMonthSlot,
  CustomerInfo,
  CurrentAddressStatus,
  CurrentEmploymentStatus,
  DrivingLicenceType,
  CustomerResidentialAddress,
  RunCreditCheckPayload,
  YearColumn,
  MonthColumn,
} from "@/models/creditCheck";

// ---------------------------------------------------------------------------
// Helpers & formatters (reused from Phoenix)
// ---------------------------------------------------------------------------

export function prettifyString(input: string | null | undefined): string {
  if (!input) return "";
  return String(input)
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

export function toTitleCase(input: string | null | undefined): string {
  if (!input) return "";
  return String(input).replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
  );
}

export function formatName(person?: CreditCheckPerson | null): string {
  if (!person) return "";
  const lastName = person.lastName || person.surname || "";
  const middle = person.middleName ? ` ${person.middleName} ` : " ";
  const full = `${person.firstName || ""}${middle}${lastName}`.replace(/\s+/g, " ").trim();
  return toTitleCase(full);
}

export function formatAddress(address?: {
  houseName?: string | null;
  houseNumber?: string | null;
  subBuilding?: string | null;
  street?: string | null;
  town?: string | null;
  postcode?: string | null;
} | null): string {
  if (!address) return "";
  const parts = [
    address.houseName,
    address.houseNumber,
    address.subBuilding,
    address.street,
    address.town,
  ]
    .filter(Boolean)
    .join(" ");
  return `${toTitleCase(parts)} ${address.postcode || ""}`.trim();
}

export function formatUnderwriteAddress(address?: {
  buildingName?: string | null;
  houseName?: string | null;
  buildingNumber?: string | null;
  houseNumber?: string | null;
  subBuilding?: string | null;
  streetName?: string | null;
  street?: string | null;
  town?: string | null;
  postcode?: string | null;
} | null): string {
  if (!address) return "";
  const bName = address.buildingName || address.houseName || "";
  const bNumber = address.buildingNumber || address.houseNumber || "";
  const subB = address.subBuilding || "";
  const str = address.streetName || address.street || "";
  const town = address.town || "";
  const parts = [bName, bNumber, subB, str, town].filter(Boolean).join(" ");
  return `${parts} ${address.postcode || ""}`.trim();
}

export function getStatusClass(status?: string | null): string {
  switch (status) {
    case "S":
      return "bg-[#fffecb] text-amber-900 border-amber-200"; // settled
    case "0":
      return "bg-[#caffc6] text-emerald-950 border-emerald-300"; // active/up to date
    case "1":
    case "2":
    case "3":
    case "4":
    case "5":
      return "bg-[#f4c180] text-amber-950 border-amber-300"; // down/arrears
    case "6":
    case "D":
    case "R":
      return "bg-[#ff7058] text-white font-medium border-rose-400"; // default/repo
    case "I":
      return "bg-[#e8e8ea] text-zinc-800 border-zinc-300"; // arrangement
    default:
      return "bg-transparent text-muted-foreground";
  }
}

export function getStatusDescription(status?: string | null): string {
  switch (status) {
    case "0":
      return "Up to date with payments";
    case "1":
    case "2":
    case "3":
    case "4":
    case "5":
    case "6":
      return `${status} payment(s) down`;
    case "I":
      return "Arrangement to pay";
    case "S":
      return "Settled";
    case "U":
      return "No payment due yet or unclassified";
    case "R":
      return "Repossession";
    case "D":
      return "Default";
    case "Q":
      return "Query";
    case "N":
      return "Inactive";
    case "Z":
      return "Never taken up";
    case "X":
      return "Transfer";
    case ".":
      return "Transaction not received this month";
    default:
      return status || "Unknown";
  }
}

export function getPaymentFrequencyDescription(paymentFrequency?: string | null): string {
  switch (paymentFrequency) {
    case "W":
      return "Weekly";
    case "F":
      return "Fortnightly";
    case "M":
      return "Monthly";
    case "Q":
      return "Quarterly";
    case "A":
      return "Annually";
    case "P":
      return "Periodically";
    default:
      return paymentFrequency || "Unknown";
  }
}

export function dateDiffDescription(
  startDate?: string | Date | null,
  endDate?: string | Date | null
): string {
  if (!startDate) return "";
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();

  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  let days = end.getDate() - start.getDate();

  if (days < 0) {
    months -= 1;
    const prevMonthDays = new Date(end.getFullYear(), end.getMonth(), 0).getDate();
    days += prevMonthDays;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) {
    years = 0;
    months = 0;
    days = 0;
  }

  return `${years} years ${months} months ${days} days`;
}

export function is247MoneyAccount(agencyName?: string | null, companyName?: string | null): boolean {
  if (agencyName && companyName) {
    if (agencyName.toLowerCase() === "callcredit") {
      return true;
    }
  }
  return false;
}

export function getYears(nowDate: Date = new Date()): YearColumn[] {
  const currentYear = nowDate.getFullYear();
  const currentMonth = nowDate.getMonth(); // 0-based
  const startDate = new Date(nowDate.getFullYear(), nowDate.getMonth() - 72, 1);
  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth();

  const years: YearColumn[] = [];
  for (let y = currentYear; y >= startYear; y--) {
    let monthCount = 12;
    if (y === currentYear) {
      monthCount = currentMonth + 1;
    } else if (y === startYear) {
      monthCount = 12 - startMonth;
    }
    years.push({ year: y, monthCount });
  }
  return years;
}

export function getMonths(nowDate: Date = new Date()): MonthColumn[] {
  const months: MonthColumn[] = [];
  const cursor = new Date(nowDate.getFullYear(), nowDate.getMonth(), 1);
  const startDate = new Date(nowDate.getFullYear(), nowDate.getMonth() - 72, 1);

  while (cursor >= startDate) {
    months.push({
      month: String(cursor.getMonth() + 1),
      year: String(cursor.getFullYear()),
      date: new Date(cursor),
    });
    cursor.setMonth(cursor.getMonth() - 1);
  }
  return months;
}

export function generateMonthPaymentSlots(
  months: MonthColumn[],
  paymentHistory?: CreditAccountPaymentHistoryItem[] | null
): CreditAccountMonthSlot[] {
  const slots: CreditAccountMonthSlot[] = [];
  let historyIdx = 0;
  const history = paymentHistory ?? [];

  for (const month of months) {
    const historyRecord = history[historyIdx];
    let slot: CreditAccountMonthSlot = {
      status: "",
      payment: 0,
      balance: 0,
      creditLimit: 0,
      date: month.date,
    };

    if (historyRecord) {
      const recDate = new Date(historyRecord.date);
      if (
        month.date.getMonth() === recDate.getMonth() &&
        month.date.getFullYear() === recDate.getFullYear()
      ) {
        slot = {
          status: historyRecord.status,
          payment: historyRecord.payment,
          balance: historyRecord.balance,
          creditLimit: historyRecord.creditLimit,
          date: recDate,
        };
        historyIdx++;
      }
    }

    slots.push(slot);
  }

  return slots;
}

export function populateAddressPaymentSlots(
  addresses: CreditCheckDetail["addresses"],
  months: MonthColumn[]
) {
  for (const address of addresses) {
    address.filteredAccounts = address.accounts;
    address.filteredElectoralRoll = address.electoralRoll;
    address.filteredCifas = address.cifas;
    address.filteredCcj = address.ccj;
    address.filteredPreviousSearches = address.previousSearches;
    address.filteredNoticeOfCorrection = address.noticeOfCorrection;

    if (address.accounts) {
      for (const account of address.accounts) {
        account.monthSlots = generateMonthPaymentSlots(months, account.paymentHistory);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Mock Data for Offline Mode
// ---------------------------------------------------------------------------

function buildMockPaymentHistory(months: MonthColumn[], pattern: string[]): CreditAccountPaymentHistoryItem[] {
  return months.slice(0, 36).map((m, idx) => ({
    date: m.date.toISOString(),
    status: pattern[idx % pattern.length] ?? "0",
    payment: 120,
    balance: Math.max(0, 2400 - idx * 60),
    creditLimit: 3000,
  }));
}

export function createMockCreditCheck(agency: CreditAgency, applicationId: number): CreditCheckDetail {
  const months = getMonths();
  return {
    id: `mock-cc-${applicationId}-${agency.toLowerCase()}`,
    agency,
    score: agency === "Equifax" ? 645 : 680,
    searchPurpose: "soft",
    agencySearchDate: new Date().toISOString(),
    person: {
      firstName: "Alexander",
      lastName: "Hunter",
      dateOfBirth: "1982-08-08T00:00:00Z",
      accountHolderType: "MainCustomer",
    },
    summary: {
      activeAccounts: 4,
      activeAccountsTotalValue: 12500,
      worstIn12Months: "0",
      worstIn36Months: "1",
      defaults: 0,
      cCJs: 0,
      cCJTotalValue: 0,
      searchesIn6Months: 2,
      activePaydayLoans: 0,
      activePaydayLoansTotalValue: 0,
      revolvingCreditLimit: 6500,
      revolvingCreditUtilisation: 1850,
    },
    addresses: [
      {
        addressType: "Current Address",
        houseNumber: "27",
        street: "Brockington Drive",
        town: "Hereford",
        postcode: "HR1 1TA",
        accounts: [
          {
            person: {
              firstName: "Alexander",
              lastName: "Hunter",
              accountHolderType: "MainCustomer",
              dateOfBirth: "1982-08-08",
            },
            accountType: "CreditCard",
            companyName: agency === "Callcredit" ? "247 Money" : "Barclays",
            companyType: "Bank",
            startDate: "2020-03-15T00:00:00Z",
            endDate: null,
            lastUpdateDate: new Date().toISOString(),
            creditLimit: 3000,
            currentBalance: 850,
            startBalance: 0,
            defaultBalance: 0,
            defaultDate: null,
            lastThreePlusDate: null,
            numberOfPayments: 48,
            paymentFrequency: "M",
            paymentAmount: 85,
            supplementaryInformation: ["Account maintained in good order"],
            paymentHistory: buildMockPaymentHistory(months, ["0", "0", "0", "0", "0", "0", "1", "0", "0"]),
          },
          {
            person: {
              firstName: "Alexander",
              lastName: "Hunter",
              accountHolderType: "MainCustomer",
              dateOfBirth: "1982-08-08",
            },
            accountType: "HirePurchase",
            companyName: "MotoNovo Finance",
            companyType: "FinanceCompany",
            startDate: "2019-06-10T00:00:00Z",
            endDate: "2023-06-10T00:00:00Z",
            lastUpdateDate: "2023-06-10T00:00:00Z",
            creditLimit: 9500,
            currentBalance: 0,
            startBalance: 9500,
            defaultBalance: 0,
            defaultDate: null,
            lastThreePlusDate: null,
            numberOfPayments: 48,
            paymentFrequency: "M",
            paymentAmount: 245,
            supplementaryInformation: ["Settled agreement"],
            paymentHistory: buildMockPaymentHistory(months, ["S", "0", "0", "0", "0", "0"]),
          },
        ],
        electoralRoll: [
          {
            person: {
              firstName: "Alexander",
              lastName: "Hunter",
              accountHolderType: "MainCustomer",
            },
            startDate: "2019-09-01T00:00:00Z",
            endDate: null,
          },
        ],
        previousSearches: [
          {
            person: {
              firstName: "Alexander",
              lastName: "Hunter",
              accountHolderType: "MainCustomer",
            },
            searchDate: "2024-01-15T00:00:00Z",
            companyType: "MotorFinance",
            searchType: "QuotationSearch",
          },
        ],
        ccj: [],
        noticeOfCorrection: [],
        cifas: [],
      },
    ],
  };
}

export const mockCustomerInfo: CustomerInfo = {
  customerId: 10661092,
  firstName: "Alexander",
  surname: "Hunter",
  dateOfBirth: "1982-08-08T00:00:00Z",
  netMonthlyIncome: 2450,
  drvLicenceTypeId: 2,
  drivingLicenceTypeName: "Full UK Driving Licence",
  residentialAddresses: [
    {
      addressId: 1,
      houseNumber: "27",
      buildingNumber: "27",
      streetName: "Brockington Drive",
      street: "Brockington Drive",
      town: "Hereford",
      postcode: "HR1 1TA",
    },
  ],
};

// ---------------------------------------------------------------------------
// Credit Check API Service (Endpoints reused from Phoenix)
// ---------------------------------------------------------------------------

export const creditCheckService = {
  formatName,
  formatAddress,
  formatUnderwriteAddress,
  prettifyString,
  toTitleCase,
  getStatusClass,
  getStatusDescription,
  getPaymentFrequencyDescription,
  dateDiffDescription,
  getYears,
  getMonths,
  generateMonthPaymentSlots,
  populateAddressPaymentSlots,
  is247MoneyAccount,

  async searchCreditCheck(applicationId: number, fromDate: Date = new Date(1900, 1, 1)): Promise<{ id: string }> {
    if (isOfflineMode()) {
      return { id: `mock-guid-${applicationId}` };
    }
    const response = await apiClient.get<{ id: string }>("/credit-checks", { params: { appId: applicationId, fromDate: fromDate.toISOString() } });
    return response.data;
  },

  async getCreditCheck(creditCheckId: string, agency: string, version: number): Promise<CreditCheckDetail> {
    if (isOfflineMode()) {
      return createMockCreditCheck((agency as CreditAgency) || "Equifax", 123);
    }
    const response = await apiClient.get<CreditCheckDetail>(`/credit-checks/${creditCheckId}/${agency}/${version}`);
    return response.data;
  },

  async getCreditCheckSummary(creditCheckId: string, agency: string, version: number): Promise<CreditCheckSummary> {
    if (isOfflineMode()) {
      const mock = createMockCreditCheck((agency as CreditAgency) || "Equifax", 123);
      return mock.summary!;
    }
    const response = await apiClient.get<CreditCheckSummary>(`/credit-checks/${creditCheckId}/${agency}/${version}/summary`);
    return response.data;
  },

  async getCreditCheckArchive(applicationId: number, agency: string): Promise<CreditCheckDetail> {
    if (isOfflineMode()) {
      return createMockCreditCheck((agency as CreditAgency) || "Equifax", applicationId);
    }
    const response = await apiClient.get<CreditCheckDetail>(`/credit-checks/archive`, { params: { applicationId, creditAgency: agency } });
    return response.data;
  },

  async runCreditCheck(agency: string, version: number, requestBody: RunCreditCheckPayload): Promise<{ id: string }> {
    if (isOfflineMode()) {
      return { id: `mock-guid-new-${Date.now()}` };
    }
    const response = await apiClient.post<{ id: string }>(`/credit-checks/${agency}/${version}`, requestBody);
    return response.data;
  },

  async reRunCreditCheck(id: string, agency: string, version: number, requestBody: RunCreditCheckPayload): Promise<unknown> {
    if (isOfflineMode()) {
      return { success: true };
    }
    const response = await apiClient.put(`/credit-checks/${id}/${agency}/${version}`, requestBody);
    return response.data;
  },

  async updateFromPatch(creditSearchId: string, appId: number): Promise<unknown> {
    if (isOfflineMode()) {
      return { success: true };
    }
    const response = await apiClient.patch(`/credit-checks/${creditSearchId}`, { appId });
    return response.data;
  },

  async getCustomerByApplicationId(applicationId: number): Promise<CustomerInfo> {
    if (isOfflineMode()) {
      return mockCustomerInfo;
    }
    const response = await apiClient.get<CustomerInfo>(`/credit-checks/customer/by-application-id`, { params: { applicationId } });
    return response.data;
  },

  async getCurrentResidentialAddress(customerId: number): Promise<CurrentAddressStatus> {
    if (isOfflineMode()) {
      return { statusDescription: "Private Tenant" };
    }
    const response = await apiClient.get<CurrentAddressStatus>(`/credit-checks/customer/${customerId}/addresses/residential/current`);
    return response.data;
  },

  async getCurrentEmploymentAddress(customerId: number): Promise<CurrentEmploymentStatus> {
    if (isOfflineMode()) {
      return { statusDescription: "Employed Full Time", occupation: "Support worker" };
    }
    const response = await apiClient.get<CurrentEmploymentStatus>(`/credit-checks/customer/${customerId}/addresses/work/current`);
    return response.data;
  },

  async getDrivingLicenceTypes(): Promise<DrivingLicenceType[]> {
    if (isOfflineMode()) {
      return [
        { drivingLicenceTypeId: 1, drivingLicenceTypeName: "Provisional UK Driving Licence" },
        { drivingLicenceTypeId: 2, drivingLicenceTypeName: "Full UK Driving Licence" },
        { drivingLicenceTypeId: 3, drivingLicenceTypeName: "EU Driving Licence" },
        { drivingLicenceTypeId: 4, drivingLicenceTypeName: "International Driving Licence" },
      ];
    }
    const response = await apiClient.get<DrivingLicenceType[]>(`/credit-checks/customer/driving-licence-types`);
    return response.data;
  },

  async getResidentialAddresses(customerId: number): Promise<CustomerResidentialAddress[]> {
    if (isOfflineMode()) {
      return mockCustomerInfo.residentialAddresses ?? [];
    }
    const response = await apiClient.get<CustomerResidentialAddress[]>(`/credit-checks/customer/${customerId}/addresses/residential`);
    return response.data;
  },
};
